import { siteApiSlice } from "../../app/siteApiSlice";

export interface MyAccountViewModel {
	rules: RulesModel;
	canEditName: boolean;
	fName: string;
	lName: string;
	emailAddress: string | undefined;
	isEmailAvailable: boolean;
	currentPassword: string | undefined;
	newPassword: string | undefined;
	confirmPassword: string | undefined;
	isPasswordRequired: boolean;
	externalLoginStatus: string;
	externalLoginErrors: string[];
	currentLogins: string[];
	otherLogins: string[];
	minimumPasswordLength: number;
	isCleverUser: boolean;
	imageUploadDisabled: boolean;
	hasImage: boolean;
	initials: string;
	userImage: string;
	canChangeOwnScoringLevel: boolean;
	level: string;
}

export interface RulesModel {
	userNameRegEx: string;
	userNameMinLength: number;
	userNameMaxLength: number;
	writerPasswordMinLength: number;
	nonWriterPasswordMinLength: number;
	passwordMaxLength: number;
	firstNameMaxLength: number;
	lastNameMaxLength: number;
	externalIdMaxLength: number;
	emailMaxLength: number;
	phoneMaxLength: number;
	zipCodeMaxLength: number;
}

export interface UserImageResponse {
	status: string;
	message: string;
	image: string;
}

export interface ForgotPasswordViewModel {
	rules: RulesModel;
	email: string;
	username: string;
	isUsernameRequired: boolean;
}

export interface ResetPasswordViewModel {
	username: string;
	password: string;
	confirmPassword: string;
	token: string;
	isCleverUser?: boolean;
	isClassLinkUser?: boolean;
}

export const myAccountApiSlice = siteApiSlice
	.enhanceEndpoints({
		addTagTypes: ["AccountInfo"],
	})
	.injectEndpoints({
		endpoints: (build) => ({
			getAccountInfo: build.query<MyAccountViewModel, void>({
				query: () => `/Account/Edit`,
				providesTags: ["AccountInfo"],
			}),
			saveAccountInfo: build.mutation<null, MyAccountViewModel>({
				query: (payload) => ({
					url: `/Account/Edit`,
					method: "POST",
					body: payload,
				}),
				transformErrorResponse: (
					response: { status: string | number; data?: unknown },
					meta,
					arg
				) => {
					console.log(`transformErrorResponse ${response.data}`);
					const message =
						typeof response.data === "string"
							? response.data
							: "An error occurred while saving your account information.";
					return message;
				},
				invalidatesTags: ["AccountInfo"],
			}),
			uploadAvatar: build.mutation<UserImageResponse, FormData>({
				query: (payload) => ({
					url: `/User/UploadUserImage`,
					method: "POST",
					body: payload,
				}),
				invalidatesTags: ["AccountInfo"],
			}),
			removeAvatar: build.mutation<UserImageResponse, { userId?: number } | void>({
				query: (payload) => ({
					url: `/User/RemoveUserImage`,
					method: "POST",
					body: payload,
				}),
				invalidatesTags: ["AccountInfo", "ManageCourse"],
			}),
			forgotPasswordInfo: build.query<ForgotPasswordViewModel, void>({
				query: () => `/Account/ForgotPassword`,
			}),
			forgotPassword: build.mutation<null, ForgotPasswordViewModel>({
				query: (payload) => ({
					url: `/Account/ForgotPassword`,
					method: "POST",
					body: payload,
				}),
			}),
			resetPassword: build.mutation<null, ResetPasswordViewModel>({
				query: (payload) => ({
					url: `/Account/ResetPassword`,
					method: "POST",
					body: payload,
				}),
			}),
		}),
	});

export const {
	useGetAccountInfoQuery,
	useSaveAccountInfoMutation,
	useUploadAvatarMutation,
	useRemoveAvatarMutation,
	useForgotPasswordInfoQuery,
	useForgotPasswordMutation,
	useResetPasswordMutation,
} = myAccountApiSlice;
